import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "@phosphor-icons/react";

/**
 * Cookie / analytics consent banner.
 *
 * Complies with:
 *  - CCPA / CPRA (California) — "Reject" is prominent, choice remembered.
 *  - GDPR / UK GDPR — explicit opt-in required, denied by default.
 *
 * Wired to Google Analytics via Consent Mode v2. Until the user picks
 * Accept, GA4 loads in "denied" mode and drops no identifying cookies.
 * We store the choice in localStorage so the banner only shows once.
 */
const STORAGE_KEY = "stableish_cookie_consent";
const REOPEN_EVENT = "stableish:reopen-cookie-banner";

/** Fire this from anywhere (e.g., a footer link) to re-open the banner. */
export function reopenCookieBanner() {
  try {
    window.dispatchEvent(new Event(REOPEN_EVENT));
  } catch {
    /* ignore */
  }
}

function updateGtagConsent(state) {
  const granted = state === "granted";
  const value = granted ? "granted" : "denied";
  try {
    const g = typeof window !== "undefined" ? window.gtag : undefined;
    if (typeof g === "function") {
      g("consent", "update", {
        ad_storage: value,
        ad_user_data: value,
        ad_personalization: value,
        analytics_storage: value,
      });
    } else if (typeof window !== "undefined" && Array.isArray(window.dataLayer)) {
      // gtag() alias not yet defined but dataLayer exists — push the same op
      window.dataLayer.push([
        "consent",
        "update",
        {
          ad_storage: value,
          ad_user_data: value,
          ad_personalization: value,
          analytics_storage: value,
        },
      ]);
    }
  } catch {
    /* consent update must never break the page */
  }

  // Mirror consent state to Meta Pixel (fbq) so it respects the same choice.
  try {
    const fbq = typeof window !== "undefined" ? window.fbq : undefined;
    if (typeof fbq === "function") {
      fbq("consent", granted ? "grant" : "revoke");
    }
  } catch {
    /* fbq updates must never break the page */
  }

  // Mirror consent state to our PostHog instance (loaded via inline
  // snippet in index.html; accessed via window.posthog).
  try {
    if (typeof window !== "undefined" && window.posthog && window.posthog.opt_in_capturing) {
      if (granted) {
        window.posthog.opt_in_capturing();
      } else {
        window.posthog.opt_out_capturing();
      }
    }
  } catch {
    /* posthog updates must never break the page */
  }

  // Mirror consent state to TikTok Pixel (ttq). We call holdConsent() on
  // init so nothing tracks until the user grants consent here.
  try {
    const ttq = typeof window !== "undefined" ? window.ttq : undefined;
    if (ttq && typeof ttq.grantConsent === "function") {
      if (granted) {
        ttq.grantConsent();
      } else {
        ttq.revokeConsent();
      }
    }
  } catch {
    /* ttq updates must never break the page */
  }
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Delay slightly so the banner doesn't fight the hero animation.
    const t = setTimeout(() => {
      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (!saved) {
          setVisible(true);
        } else {
          // Re-apply the persisted choice on every load so the region-based
          // default doesn't silently override it (esp. for US visitors who
          // previously tapped Reject — default is 'granted' outside EEA).
          updateGtagConsent(saved === "granted" ? "granted" : "denied");
        }
      } catch {
        setVisible(true);
      }
    }, 600);

    // Allow "Cookie settings" link in the footer (or anywhere) to
    // re-open the banner via a custom event.
    const onReopen = () => setVisible(true);
    window.addEventListener(REOPEN_EVENT, onReopen);

    return () => {
      clearTimeout(t);
      window.removeEventListener(REOPEN_EVENT, onReopen);
    };
  }, []);

  const choose = (state) => {
    try {
      localStorage.setItem(STORAGE_KEY, state);
    } catch {
      /* private mode / storage full — ignore */
    }
    updateGtagConsent(state);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      data-testid="cookie-banner"
      className="fixed inset-x-0 bottom-0 z-[60] px-3 pb-3 md:px-6 md:pb-6 pointer-events-none"
    >
      <div
        className="pointer-events-auto max-w-[900px] mx-auto border border-white/15 bg-[#0a0a0a]/95 shadow-[0_-20px_40px_-20px_rgba(0,0,0,0.5)]"
        style={{ backdropFilter: "blur(14px)" }}
      >
        <div className="p-4 md:p-6 flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#FF2A2A] mb-1.5">
              // cookies
            </div>
            <p className="text-[13px] md:text-sm text-white/80 leading-relaxed">
              <span className="md:hidden">
                Analytics cookies help us see what actually helps ER &amp; ICU
                nurses. No personalised ads, no data selling.{" "}
              </span>
              <span className="hidden md:inline">
                We use cookies for analytics (Google Analytics, PostHog, Meta,
                TikTok) so we can see what content actually helps ER &amp; ICU
                nurses. No ads targeted to individuals, no selling of your
                data.{" "}
              </span>
              Read our{" "}
              <Link
                to="/privacy"
                className="text-[#FF2A2A] hover:text-[#FF4D4D] underline decoration-[#FF2A2A]/40 underline-offset-2"
                data-testid="cookie-banner-privacy-link"
              >
                Privacy Policy
              </Link>
              .
            </p>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => choose("denied")}
              data-testid="cookie-banner-reject"
              className="flex-1 md:flex-none font-mono text-[10px] uppercase tracking-[0.22em] border border-white/20 hover:border-white/50 text-white/80 hover:text-white px-4 py-3 md:py-2.5 transition-colors"
            >
              Reject
            </button>
            <button
              onClick={() => choose("granted")}
              data-testid="cookie-banner-accept"
              className="flex-1 md:flex-none font-mono text-[10px] uppercase tracking-[0.22em] bg-[#FF2A2A] hover:bg-[#FF4D4D] text-white px-4 py-3 md:py-2.5 transition-colors"
            >
              Accept
            </button>
            <button
              onClick={() => choose("denied")}
              aria-label="Close and reject cookies"
              data-testid="cookie-banner-close"
              className="text-white/50 hover:text-white ml-1 p-1"
            >
              <X size={14} weight="bold" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
